import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { siteAPI } from '../services/api';

const SiteDetailsPage = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [budgetDetails, setBudgetDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSiteDetails();
  }, [siteId]);

  const fetchSiteDetails = async () => {
    try {
      setLoading(true);
      const response = await siteAPI.getById(siteId);
      setSite(response.data.data.site);

      // Calculate budget details if budget is set
      if (response.data.data.site.projectedBudget > 0) {
        calculateBudgetDetails(response.data.data.site);
      }
    } catch (err) {
      setError('Failed to load site details');
      console.error('Error fetching site:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateBudgetDetails = (siteData) => {
    // For now, set actualSpending to 0 - will be calculated from materials
    const actualSpending = 0;
    const remaining = siteData.projectedBudget - actualSpending;
    const percentageUsed = siteData.projectedBudget > 0
      ? (actualSpending / siteData.projectedBudget) * 100
      : 0;

    let budgetStatus = 'under_budget';
    let isOverBudget = false;
    let isNearLimit = false;

    if (percentageUsed >= 100) {
      budgetStatus = 'over_budget';
      isOverBudget = true;
    } else if (percentageUsed >= siteData.budgetAlertThreshold) {
      budgetStatus = 'near_limit';
      isNearLimit = true;
    }

    setBudgetDetails({
      projectValue: parseFloat(siteData.projectValue || 0),
      projectedBudget: parseFloat(siteData.projectedBudget || 0),
      actualSpending,
      remaining,
      percentageUsed,
      budgetStatus,
      alertThreshold: siteData.budgetAlertThreshold || 80,
      isOverBudget,
      isNearLimit
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'in_progress':
        return 'success';
      case 'completed':
        return 'default';
      case 'on_hold':
        return 'warning';
      case 'stopped':
        return 'error';
      default:
        return 'default';
    }
  };

  const getBudgetStatusConfig = (status) => {
    switch (status) {
      case 'over_budget':
        return {
          label: 'Over Budget',
          color: 'error',
          icon: '❌'
        };
      case 'near_limit':
        return {
          label: 'Near Limit',
          color: 'warning',
          icon: '⚠️'
        };
      case 'under_budget':
        return {
          label: 'Under Budget',
          color: 'success',
          icon: '✅'
        };
      default:
        return {
          label: 'No Budget Set',
          color: 'default',
          icon: '➖'
        };
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'error';
    if (percentage >= 85) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !site) {
    return (
      <Box>
        <Alert severity="error">{error || 'Site not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sites')}
          sx={{ mt: 2 }}
        >
          Back to Sites
        </Button>
      </Box>
    );
  }

  const budgetStatusConfig = budgetDetails ? getBudgetStatusConfig(budgetDetails.budgetStatus) : null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/sites')}
          sx={{ mb: 2 }}
        >
          Back to Sites
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">{site.siteName}</Typography>
          <Chip
            label={site.status}
            color={getStatusColor(site.status)}
            size="medium"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Site Information Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Site Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="body1">{site.location}</Typography>
              </Box>
            </Box>

            {site.clientName && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Client</Typography>
                  <Typography variant="body1">{site.clientName}</Typography>
                </Box>
              </Box>
            )}

            {site.projectCode && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Project Code</Typography>
                <Typography variant="body1">{site.projectCode}</Typography>
              </Box>
            )}

            {site.startDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography variant="body1">
                    {new Date(site.startDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            )}

            {site.assignedUsers && site.assignedUsers.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Supervisors</Typography>
                <Box sx={{ mt: 1 }}>
                  {site.assignedUsers.map((user, index) => (
                    <Chip
                      key={index}
                      label={user.name}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Budget Information Card */}
        <Grid item xs={12} md={8}>
          {budgetDetails && budgetDetails.projectedBudget > 0 ? (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <WalletIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Budget Information</Typography>
                </Box>
                <Chip
                  label={`${budgetStatusConfig.icon} ${budgetStatusConfig.label}`}
                  color={budgetStatusConfig.color}
                  size="medium"
                />
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Budget Utilization
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={
                      budgetDetails.percentageUsed >= 100 ? 'error.main' :
                        budgetDetails.percentageUsed >= 85 ? 'warning.main' :
                          'success.main'
                    }
                  >
                    {budgetDetails.percentageUsed.toFixed(2)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(budgetDetails.percentageUsed, 100)}
                  color={getProgressColor(budgetDetails.percentageUsed)}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                {budgetDetails.percentageUsed > 100 && (
                  <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                    ⚠️ Budget exceeded by {(budgetDetails.percentageUsed - 100).toFixed(2)}%
                  </Typography>
                )}
              </Box>

              {/* Budget Details Grid */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Project Value
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(budgetDetails.projectValue)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Card variant="outlined" sx={{ bgcolor: 'info.50' }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Projected Budget
                      </Typography>
                      <Typography variant="h6" color="info.main">
                        {formatCurrency(budgetDetails.projectedBudget)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Card
                    variant="outlined"
                    sx={{
                      bgcolor: budgetDetails.isOverBudget ? 'error.50' :
                        budgetDetails.isNearLimit ? 'warning.50' :
                          'success.50'
                    }}
                  >
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Actual Spending
                      </Typography>
                      <Typography
                        variant="h6"
                        color={
                          budgetDetails.isOverBudget ? 'error.main' :
                            budgetDetails.isNearLimit ? 'warning.main' :
                              'success.main'
                        }
                      >
                        {formatCurrency(budgetDetails.actualSpending)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Card
                    variant="outlined"
                    sx={{ bgcolor: budgetDetails.remaining < 0 ? 'error.50' : 'grey.100' }}
                  >
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Remaining
                      </Typography>
                      <Typography
                        variant="h6"
                        color={budgetDetails.remaining < 0 ? 'error.main' : 'text.primary'}
                      >
                        {formatCurrency(budgetDetails.remaining)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Alert Threshold */}
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Alert Threshold:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {budgetDetails.alertThreshold}%
                  </Typography>
                </Box>
              </Box>

              {/* Budget Alerts */}
              {budgetDetails.isOverBudget && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  ⚠️ Budget Exceeded! Spending is {budgetDetails.percentageUsed.toFixed(2)}% of budget.
                </Alert>
              )}

              {budgetDetails.isNearLimit && !budgetDetails.isOverBudget && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  ⚠️ Approaching Budget Limit! {budgetDetails.percentageUsed.toFixed(2)}% of budget used.
                </Alert>
              )}

              {/* Budget Notes */}
              {site.notes && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Budget Notes:
                  </Typography>
                  <Typography variant="body2">
                    {site.notes}
                  </Typography>
                </Box>
              )}
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <WalletIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Budget Set
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Budget tracking is not enabled for this site
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Material Entries Section - Placeholder for future */}
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Material Entries
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            Material entries will be displayed here
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default SiteDetailsPage;
